<!-- The single-remark panel is laid out exactly like the report's Activity
     panel (activity-item-details): a 40px-gapped column with a bare heading.
     The other panels keep the header + sub-heading block. -->
<div [ngClass]="isSingleRemark ? 'remark-panel gap-[40px]' : 'justify-between'" class="wrapper flex flex-col">
  @if (isSingleRemark) {
    <!-- Bare heading, as on the report's Activity panel: the date is already
         on the WORK DAY row of the record this panel was opened from. -->
    <div class="heading text-type-2" data-testid="daily-diary-form-heading">{{ panelTitle }}</div>
  } @else {
    <div class="header-section flex flex-row justify-between items-start mb-[20px]">
      <div>
        <div class="heading text-type-2" data-testid="daily-diary-form-heading">
          {{ panelTitle }}
        </div>
        <div class="sub-heading text-type-10">
          @if (diary?.diary_date) {
            <span class="diary-date-sub">{{ diary.diary_date | date:'MMMM d, y' }}</span>
          }
        </div>
      </div>
      <!-- No close icon here: the right panel host (projects-wrapper) already
           renders one in its `.close-block`, so a second X in this header was a
           duplicate control for the same action. Delete lives on the record's
           kebab, not on a component panel. -->
    </div>
  }

  @if (loading) {
    <div class="loading text-type-8">Loading…</div>
  } @else {
    <form [formGroup]="form" class="details flex-1">
      <!-- WORK DAY: the day-level fields, matching the report's workday block. -->
      @if (showsSection('workday')) {
        <div class="section flex flex-col gap-[12px] mb-[24px]">
          <app-date-picker
            [parentFormGroup]="form"
            controlName="diary_date"
            label="Diary date"
            placeholder="MM/DD/YYYY"
          ></app-date-picker>

          <app-check-box
            [parentFormGroup]="form"
            controlName="contractor_working"
            label="Contractor was working on this day"
          ></app-check-box>
        </div>
      }

      <!-- Weather -->
      @if (showsSection('weather')) {
      <div class="section mb-[24px]">
        <div class="grid grid-cols-2 gap-x-[12px]">
          <app-number-input
            [parentFormGroup]="weatherGroup"
            [isDecimalAllowed]="true"
            [decimalPlaces]="1"
            [min]="-200"
            [max]="200"
            controlName="avgtemp"
            label="Avg temp (°F)"
          ></app-number-input>
          <app-number-input
            [parentFormGroup]="weatherGroup"
            [isDecimalAllowed]="true"
            [decimalPlaces]="1"
            [min]="0"
            [max]="500"
            controlName="wind"
            label="Wind (MPH)"
          ></app-number-input>
          <app-number-input
            [parentFormGroup]="weatherGroup"
            [isDecimalAllowed]="true"
            [decimalPlaces]="1"
            [min]="-200"
            [max]="200"
            controlName="maxtemp"
            label="Max temp (°F)"
          ></app-number-input>
          <app-number-input
            [parentFormGroup]="weatherGroup"
            [isDecimalAllowed]="true"
            [decimalPlaces]="1"
            [min]="-200"
            [max]="200"
            controlName="mintemp"
            label="Min temp (°F)"
          ></app-number-input>
        </div>
        <app-text-input
          [parentFormGroup]="weatherGroup"
          [maxLength]="120"
          controlName="condition_text"
          label="Sky condition"
          placeholder="e.g. Partly cloudy"
        ></app-text-input>
        <app-text-area
          [parentFormGroup]="weatherGroup"
          controlName="note"
          label="Notes"
          placeholder="Anything worth noting about the weather"
        ></app-text-area>
      </div>
      }

      <!-- ONE remark, like the daily report's "Remark" panel: the section in
           the main body lists the entries and clicking one opens just that
           entry here. -->
      @if (isSingleRemark) {
        @if (activeRemarkGroup) {
          <!-- Same structure, field components and spacing as the report's
               Activity panel (activity-item-details): no box around the
               fields, a 40px-gapped column. -->
          <div class="single-remark flex flex-col gap-[40px]">
            <!-- `title` is the body text, so it gets a textarea rather than a
                 one-line input; 1024 is the column width. -->
            <app-text-area
              [error]="remarkDetailError"
              [parentFormGroup]="activeRemarkGroup"
              [maxLength]="1024"
              controlName="title"
              label="Detail"
            ></app-text-area>

            <!-- Always the select, like ACTIVITY TYPE on the report: the form
                 is disabled on a submitted diary, so it renders read-only with
                 the current value. -->
            <app-dropdown
              [parentFormGroup]="activeRemarkGroup"
              [options]="activeRemarkTypeOptions"
              class="mb-[20px]"
              controlName="type"
              label="Remark type"
            ></app-dropdown>

            <!-- No DESCRIPTION field: mobile stores the whole remark in
                 `title` (DETAIL) and every real remark has an empty
                 description, so a second textarea only duplicated it. The
                 control stays in the form group for api compatibility. -->

            <!-- The report's shared attachment grid (MEDIA label, tiles, the
                 trash affordance and the full-screen carousel). -->
            @if (activeRemarkGroup.get('pictures')?.value?.length) {
              <app-pictures-section
                (onDelete)="removePicture(remarkUuid!, $event?.id)"
                [isEditable]="editable()"
                [pictures]="activeRemarkGroup.get('pictures')?.value"
                class="pictures-section mb-[40px]"
              />
            }

            <!-- The report's shared LOCATION / STATION card. Bound to this
                 remark's FormGroup, so its own click-to-edit dialogs and
                 "Clear" write straight back into the entry being edited. -->
            @if (activeRemarkHasLocation) {
              <app-location-station-card
                (onDelete)="deleteLocation()"
                [isEditable]="editable()"
                [latitude]="activeRemarkGroup.get('latitude')?.value"
                [longitude]="activeRemarkGroup.get('longitude')?.value"
                [locationText]="activeRemarkGroup.get('location')?.value"
                [stationRange]="activeRemarkGroup.get('station_range')?.value"
                [form]="activeRemarkGroup"
                [recordLabel]="'remark'"
                class="location-section mb-[40px]"
                data-testid="remark-location-card"
              />
            }
          </div>
        } @else {
          <div class="loading text-type-8">Loading…</div>
        }
      }

      <!-- The single-remark panel attaches pictures through the round + in its
           actions row, so the input lives outside the remark block. -->
      <input
        #fileInput
        (change)="onFileSelected($event)"
        accept="image/*"
        class="hidden"
        multiple
        type="file"
      />
    </form>

    @if (isSingleRemark) {
      <!-- Same actions row as the report's per-entry panels
           (app-actions-section): a full-width Save, a round + for
           attachments and a round ... for add/delete, pinned to the bottom of
           the panel. Rebuilt here rather than reused because that component
           drives the daily REPORT — it saves through DailyReportService and
           reads `expandedSection` to decide what is editable, neither of which
           applies to a diary. On a submitted diary it renders the way the
           report's does when the report is not editable: every control
           present but disabled. -->
      <div class="actions-section flex gap-[20px] w-[455px]">
        @if (editable()) {
          <app-primary-button
            (handleClick)="save()"
            [isDisabled]="saving || !form.dirty || activeRemarkIncomplete"
            class="save-button"
            data-testid="daily-diary-save-btn"
          >
            {{ saving ? 'Saving…' : 'Save' }}
          </app-primary-button>
          <div
            [matMenuTriggerFor]="attachMenu"
            class="action-button add-button"
            data-testid="remark-add-menu"
          >
            <mat-icon class="add" svgIcon="plus" />
          </div>
          <div
            [matMenuTriggerFor]="remarkMenu"
            class="action-button menu-button"
            data-testid="remark-option-menu"
          >
            <mat-icon class="menu">more_horiz</mat-icon>
          </div>
          <!-- Same entries as the report toolbar's add menu (minus Comment,
               which a remark has no use for). "Station range" follows the
               report's gate — see `stationRangeEnabled`. -->
          <mat-menu #attachMenu="matMenu">
            <button (click)="triggerUpload(remarkUuid!)" mat-menu-item type="button">
              <mat-icon svgIcon="camera-outline" />
              Picture
            </button>
            <button
              (click)="openLocationPicker()"
              data-testid="remark-add-location"
              mat-menu-item
              type="button"
            >
              <mat-icon svgIcon="marker-outline" />
              Add location
            </button>
            @if (hasPublishedSheets) {
              <!-- W49: as on mobile, the picker on its Drawing tab -->
              <button (click)="markOnDrawing()" data-testid="remark-mark-on-drawing" mat-menu-item type="button">
                <mat-icon svgIcon="markup-line" />
                Mark on drawing
              </button>
            }
            @if (stationRangeEnabled) {
              <button
                (click)="openStationRangeDialog()"
                data-testid="remark-add-station"
                mat-menu-item
                type="button"
              >
                <mat-icon svgIcon="plus" />
                Station range
              </button>
            }
          </mat-menu>
          <!-- Delete only: a remark is added from the floating +, the one
               way in, rather than from two places. -->
          <mat-menu #remarkMenu="matMenu">
            <button (click)="deleteActiveRemark()" mat-menu-item type="button">
              <mat-icon svgIcon="trash" />
              Delete
            </button>
          </mat-menu>
        } @else {
          <app-primary-button
            [isDisabled]="true"
            class="save-button"
            data-testid="daily-diary-save-btn"
          >
            Save
          </app-primary-button>
          <div
            [class.disabled]="true"
            class="action-button add-button"
            data-testid="remark-add-menu"
          >
            <mat-icon class="add" svgIcon="plus" />
          </div>
          <div
            [class.disabled]="true"
            class="action-button menu-button"
            data-testid="remark-option-menu"
          >
            <mat-icon class="menu">more_horiz</mat-icon>
          </div>
        }
      </div>
    } @else if (editable()) {
      <!-- Every panel is a single-purpose editor now, so one Save — full width,
           the same treatment the report's component panels give it. Submitting
           the diary lives on the record's kebab next to Delete. -->
      <div class="actions-row flex gap-[20px] w-full mt-[20px]">
        <app-primary-button
          (handleClick)="save()"
          [isDisabled]="saving"
          class="save-button"
          data-testid="daily-diary-save-btn"
        >
          {{ saving ? 'Saving…' : 'Save' }}
        </app-primary-button>
      </div>
    }
  }
</div>
